(() => {
  if (customElements.get('carisma-mobile-menu')) return;

  const triggerSelector = 'header-drawer > details > summary, #Details-menu-drawer-container > summary';
  const triggerAttributes = ['aria-controls', 'aria-haspopup', 'aria-expanded'];
  let activeOwner = null;

  class CarismaMobileMenu extends HTMLElement {
    static get observedAttributes() { return ['data-enabled']; }

    connectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected && !this.controller) this.initialize();
      });
    }

    attributeChangedCallback() {
      if (this.controller) this.sync();
    }

    initialize() {
      this.controller = new AbortController();
      this.mobile = matchMedia('(max-width: 991px)');
      this.dialog = this.querySelector('[data-cmm-dialog]');
      this.panel = this.querySelector('.cmm-panel');
      this.on = (target, type, callback, options = {}) => {
        target?.addEventListener(type, callback, { ...options, signal: this.controller.signal });
      };
      this.on(this.mobile, 'change', () => this.sync());
      this.on(document, 'click', (event) => {
        const target = event.target instanceof Element ? event.target : event.target.parentElement;
        const summary = target?.closest(triggerSelector);
        if (summary !== this.trigger || activeOwner !== this || !this.eligible()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.dialog.open) this.close();
        else this.open();
      }, { capture: true });
      this.on(this.querySelector('[data-cmm-close]'), 'click', () => this.close());
      this.on(this.dialog, 'cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.on(this.dialog, 'close', () => {
        if (!this.dialog.open) this.close(false);
      });
      this.on(this.dialog, 'click', (event) => {
        const bounds = this.panel?.getBoundingClientRect();
        if (bounds && (event.clientX < bounds.left || event.clientX > bounds.right ||
          event.clientY < bounds.top || event.clientY > bounds.bottom)) this.close();
        else if (event.target.closest('a[href]')) this.close();
      });
      this.on(document, 'shopify:section:load', () => this.scheduleSync());
      this.on(document, 'shopify:section:unload', (event) => {
        if (event.target === this || event.target.contains(this)) this.disconnectedCallback();
        else if (this.trigger && event.target.contains(this.trigger)) this.deactivate();
      });
      this.on(document, 'shopify:section:select', (event) => {
        if (this.dataset.designMode === 'true' && (event.target === this || event.target.contains(this))) this.open();
      });
      this.on(document, 'shopify:block:select', (event) => {
        if (this.dataset.designMode !== 'true' || !this.contains(event.target)) return;
        this.open();
        if (!this.dialog?.open) return;
        let details = event.target.closest('details');
        while (details && this.contains(details)) {
          details.open = true;
          details = details.parentElement?.closest('details');
        }
        this.revealItem(event.target);
      });

      this.nativeObserver = new MutationObserver(() => this.scheduleSync());
      this.contentObserver = new MutationObserver(() => this.scheduleSync());
      this.contentObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
      this.headerObserver = new MutationObserver((records) => {
        if (this.trigger && !this.trigger.isConnected) return this.scheduleSync();
        const headerChanged = records.some((record) => {
          if (this.contains(record.target)) return false;
          if (record.target instanceof Element && record.target.closest('header-drawer, .section-header')) return true;
          return [...record.addedNodes, ...record.removedNodes].some((node) => node instanceof Element &&
            (node.matches(triggerSelector) || node.matches('header-drawer') || node.querySelector(triggerSelector)));
        });
        if (headerChanged) this.scheduleSync();
      });
      this.headerObserver.observe(document.body, { childList: true, subtree: true });
      this.sync();
    }

    scheduleSync() {
      if (this.syncFrame || !this.controller) return;
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        if (this.isConnected) this.sync();
      });
    }

    meaningfulNavigation() {
      return [...this.querySelectorAll('[data-cmm-navigation] a[href]')].some((link) => {
        const href = link.getAttribute('href').trim();
        return href && href !== '#' && !/^(?:javascript|data):/i.test(href);
      });
    }

    eligible() {
      // Dawn owns its focus trap and body classes until its native drawer finishes closing.
      return this.isConnected && this.dataset.enabled === 'true' && this.mobile?.matches &&
        this.dialog?.id && typeof this.dialog.showModal === 'function' && this.panel &&
        this.trigger?.isConnected && !this.trigger.parentElement.open &&
        !this.trigger.parentElement.classList.contains('menu-opening') && this.meaningfulNavigation();
    }

    sync() {
      const trigger = document.querySelector(triggerSelector);
      if (trigger !== this.trigger) {
        this.deactivate();
        this.nativeObserver.disconnect();
        this.trigger = trigger;
        if (trigger?.parentElement) this.nativeObserver.observe(trigger.parentElement, { attributes: true, attributeFilter: ['open', 'class'] });
      }
      if (!this.eligible() || (activeOwner && activeOwner !== this && activeOwner.isConnected)) {
        this.deactivate();
        return;
      }
      activeOwner = this;
      if (!this.originalAttributes) {
        this.originalAttributes = new Map(triggerAttributes.map((name) => [name, this.trigger.getAttribute(name)]));
      }
      this.trigger.setAttribute('aria-controls', this.dialog.id);
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-expanded', String(this.dialog.open));
      document.documentElement.dataset.carismaMobileMenu = 'ready';
    }

    deactivate() {
      this.close();
      if (this.originalAttributes && this.trigger) {
        this.originalAttributes.forEach((value, name) => {
          if (value === null) this.trigger.removeAttribute(name);
          else this.trigger.setAttribute(name, value);
        });
      }
      this.originalAttributes = null;
      if (activeOwner === this) {
        activeOwner = null;
        delete document.documentElement.dataset.carismaMobileMenu;
      }
    }

    open() {
      this.sync();
      if (activeOwner !== this || !this.eligible() || this.dialog.open) return;
      this.opener = this.trigger;
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      this.previousOverflow = { value: document.body.style.getPropertyValue('overflow'), priority: document.body.style.getPropertyPriority('overflow') };
      this.dialog.showModal();
      document.body.style.setProperty('overflow', 'hidden');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-cmm-close]')?.focus({ preventScroll: true });
      window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
    }

    close(closeDialog = true) {
      if (closeDialog && this.dialog?.open) this.dialog.close();
      if (this.originalAttributes && this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      if (this.previousOverflow) {
        if (this.previousOverflow.value) document.body.style.setProperty('overflow', this.previousOverflow.value, this.previousOverflow.priority);
        else document.body.style.removeProperty('overflow');
        this.previousOverflow = null;
      }
      if (this.opener?.isConnected && this.opener.getClientRects().length) this.opener.focus({ preventScroll: true });
      this.opener = null;
      if (this.scrollPosition) {
        window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
        this.scrollPosition = null;
      }
    }

    revealItem(item) {
      if (!this.panel) return;
      const panelBounds = this.panel.getBoundingClientRect();
      const itemBounds = item.getBoundingClientRect();
      const distance = itemBounds.top < panelBounds.top ? itemBounds.top - panelBounds.top - 20 :
        itemBounds.bottom > panelBounds.bottom ? itemBounds.bottom - panelBounds.bottom + 20 : 0;
      if (distance) this.panel.scrollBy({ top: distance, behavior: 'instant' });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.syncFrame);
      this.syncFrame = null;
      this.nativeObserver?.disconnect();
      this.contentObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.deactivate();
      this.trigger = null;
    }
  }

  customElements.define('carisma-mobile-menu', CarismaMobileMenu);
})();
